import { z } from 'zod';

export function isZodString(s: string): s is string {
  return z.string().safeParse(s).success;
}

export function isValidZodLiteralUnion<T extends z.ZodLiteral<unknown>>(
  literals: T[],
): literals is [T, T, ...T[]] {
  return literals.length >= 2;
}

export function constructZodLiteralUnionType<T extends z.ZodLiteral<unknown>>(
  literals: T[],
) {
  if (!isValidZodLiteralUnion(literals)) {
    throw new Error(
      'Literals passed do not meet the criteria for constructing a union schema, the minimum length is 2',
    );
  }

  return z.union(literals);
}

// Schema for Date.toISOString() format
export const IsoDateStringSchema = z.coerce
  .string()
  .regex(
    /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}:\d{2}(\.\d{3,6})?(Z|([+-]\d{2}(:\d{2})?))?$/,
  );

// Schema for YYYY-MM-DD format
export const SimpleDateStringSchema = z.coerce
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/);

// Schema for HH:MM:SS time format
export const TimeSchema = z
  .string()
  .regex(/^(2[0-3]|[01][0-9]):([0-5][0-9]):([0-5][0-9])$/);

export const Int4RangeTuple = z.tuple([z.number(), z.number()]);

// Schema for Postgres int4range
export const TupleFromInt4Range = z.coerce
  .string()
  .min(2)
  .transform(val => {
    const int4rangeRegex = /^\[(-?\d+),(-?\d+)\)$/;
    const match = int4rangeRegex.exec(val);

    if (!Array.isArray(match) || match.length < 1) {
      // Discard invalid values
      return [];
    }

    // Extract the two integers and return as a tuple
    const start: number = parseInt(match[1], 10);
    const end: number = parseInt(match[2], 10);

    return [start, end];
  })
  .pipe(Int4RangeTuple);

export const Int4RangeFromTuple = Int4RangeTuple.transform(
  val => `[${val[0]},${val[1]})`,
);
